import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import { CityComparisonReport } from '../../types/cityComparison';
import {
  Loader2,
  CircleAlert,
  ArrowRight,
  Scale,
  Home,
  Train,
  Gem,
  Compass,
  ChevronRight,
  Wallet,
  Building2,
  Briefcase,
  Share2,
  Sparkles,
} from 'lucide-react';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
} from 'react-share';
import PseoEmailCapture from '../../components/pseo/PseoEmailCapture';
import { ensureDatasetDescriptions } from '../../lib/structuredData';

type ComparisonLens = 'all' | 'relocation' | 'financial' | 'lifestyle';

const winnerPill = (winner: 'city_a' | 'city_b' | 'tie') => {
  if (winner === 'city_a') return 'A';
  if (winner === 'city_b') return 'B';
  return 'Tie';
};

const metricLensMap: Record<string, ComparisonLens> = {
  'Median House Price': 'financial',
  'Average Rent (1-bed)': 'financial',
  'Average Commute Time': 'relocation',
  'Primary Schools (Est.)': 'lifestyle',
};

const CityComparisonPage: React.FC = () => {
  const { comparisonSlug } = useParams<{ comparisonSlug: string }>();
  const [data, setData] = useState<CityComparisonReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lens, setLens] = useState<ComparisonLens>('all');
  const [activeReason, setActiveReason] = useState<'young' | 'families' | 'investors'>('young');

  useEffect(() => {
    if (!comparisonSlug) {
      setError('Comparison slug is missing.');
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(
          `${CLOUD_FUNCTIONS_BASE_URL}/getCityComparisonReport?slug=${encodeURIComponent(comparisonSlug)}`
        );
        const result = await response.json();
        if (!result.success || !result.data) {
          setError('Comparison report not found.');
          return;
        }
        setData(result.data as CityComparisonReport);
      } catch (e) {
        setError('Unable to load comparison report.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [comparisonSlug]);

  const title = useMemo(() => {
    if (!data) return 'City Comparison';
    return `${data.city_a.name} vs ${data.city_b.name}`;
  }, [data]);
  const schema = useMemo(
    () => ensureDatasetDescriptions(data?.schema ?? null, data?.seo_content?.meta_desc ?? ''),
    [data?.schema, data?.seo_content?.meta_desc]
  );

  if (loading) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center bg-slate-50">
        <Loader2 className="h-10 w-10 animate-spin text-emerald-600 mb-3" />
        <p className="font-bold text-slate-500">Loading city comparison...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-2xl mx-auto my-20 px-4">
        <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center">
          <CircleAlert className="h-16 w-16 text-amber-500 mx-auto mb-6" />
          <h1 className="text-3xl font-black text-slate-900 mb-3">Comparison Unavailable</h1>
          <p className="text-slate-600 mb-8">{error}</p>
          <Link
            to="/city-comparisons"
            className="inline-block rounded-2xl bg-emerald-600 text-white px-8 py-4 font-black hover:bg-emerald-700"
          >
            Browse City Comparisons
          </Link>
        </div>
      </div>
    );
  }

  const canonicalUrl = `https://rentsmart.ie/city-comparisons/${data.slug}`;
  const shareUrl = typeof window !== 'undefined' ? window.location.href : canonicalUrl;
  const shareText = `Compare ${data.city_a.name} vs ${data.city_b.name} on RentSmart.ie for cost of living, rent, commute and lifestyle in Ireland.`;
  const cityARentPath = `/browse?listingIntent=rent&location=${encodeURIComponent(data.city_a.name)}`;
  const cityBRentPath = `/browse?listingIntent=rent&location=${encodeURIComponent(data.city_b.name)}`;
  const cityASalePath = `/browse?listingIntent=sale&location=${encodeURIComponent(data.city_a.name)}`;
  const cityBSalePath = `/browse?listingIntent=sale&location=${encodeURIComponent(data.city_b.name)}`;
  const filteredRows =
    lens === 'all'
      ? data.at_a_glance_rows
      : data.at_a_glance_rows.filter((row) => metricLensMap[row.label] === lens);

  const quickWins = [
    {
      title: 'Lower Rent',
      icon: <Wallet className="h-4 w-4" />,
      winner:
        data.metrics.city_a.average_rent_1bed < data.metrics.city_b.average_rent_1bed
          ? data.city_a.name
          : data.metrics.city_a.average_rent_1bed > data.metrics.city_b.average_rent_1bed
            ? data.city_b.name
            : 'Tie',
      detail: `${data.city_a.name}: €${data.metrics.city_a.average_rent_1bed.toLocaleString()} vs ${data.city_b.name}: €${data.metrics.city_b.average_rent_1bed.toLocaleString()}`,
    },
    {
      title: 'Lower House Price',
      icon: <Building2 className="h-4 w-4" />,
      winner:
        data.metrics.city_a.median_house_price < data.metrics.city_b.median_house_price
          ? data.city_a.name
          : data.metrics.city_a.median_house_price > data.metrics.city_b.median_house_price
            ? data.city_b.name
            : 'Tie',
      detail: `${data.city_a.name}: €${data.metrics.city_a.median_house_price.toLocaleString()} vs ${data.city_b.name}: €${data.metrics.city_b.median_house_price.toLocaleString()}`,
    },
    {
      title: 'Stronger Career Signal',
      icon: <Briefcase className="h-4 w-4" />,
      winner:
        data.metrics.city_a.career_score > data.metrics.city_b.career_score
          ? data.city_a.name
          : data.metrics.city_a.career_score < data.metrics.city_b.career_score
            ? data.city_b.name
            : 'Tie',
      detail: `${data.city_a.name}: ${data.metrics.city_a.career_score} vs ${data.city_b.name}: ${data.metrics.city_b.career_score}`,
    },
    {
      title: 'Commute Ease',
      icon: <Train className="h-4 w-4" />,
      winner:
        data.metrics.city_a.avg_commute_minutes < data.metrics.city_b.avg_commute_minutes
          ? data.city_a.name
          : data.metrics.city_a.avg_commute_minutes > data.metrics.city_b.avg_commute_minutes
            ? data.city_b.name
            : 'Tie',
      detail: `${data.city_a.name}: ${data.metrics.city_a.avg_commute_minutes} min vs ${data.city_b.name}: ${data.metrics.city_b.avg_commute_minutes} min`,
    },
  ];

  const winnerReasonCards = data.ai_enrichment?.winner_breakdown
    ? [
        {
          key: 'young' as const,
          label: 'Young Professionals',
          tone: 'emerald',
          text: data.ai_enrichment.winner_breakdown.young_professionals_reason,
        },
        {
          key: 'families' as const,
          label: 'Families',
          tone: 'sky',
          text: data.ai_enrichment.winner_breakdown.families_reason,
        },
        {
          key: 'investors' as const,
          label: 'Investors',
          tone: 'violet',
          text: data.ai_enrichment.winner_breakdown.investors_reason,
        },
      ]
    : [];

  const activeReasonData =
    winnerReasonCards.find((item) => item.key === activeReason) || winnerReasonCards[0];

  const getMetricNumbers = (label: string): { a: number; b: number; lowerIsBetter: boolean } => {
    if (label === 'Median House Price') {
      return {
        a: data.metrics.city_a.median_house_price,
        b: data.metrics.city_b.median_house_price,
        lowerIsBetter: true,
      };
    }
    if (label === 'Average Rent (1-bed)') {
      return {
        a: data.metrics.city_a.average_rent_1bed,
        b: data.metrics.city_b.average_rent_1bed,
        lowerIsBetter: true,
      };
    }
    if (label === 'Average Commute Time') {
      return {
        a: data.metrics.city_a.avg_commute_minutes,
        b: data.metrics.city_b.avg_commute_minutes,
        lowerIsBetter: true,
      };
    }
    return {
      a: data.metrics.city_a.primary_schools,
      b: data.metrics.city_b.primary_schools,
      lowerIsBetter: false,
    };
  };

  return (
    <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-x-hidden bg-slate-50">
      <Helmet>
        <title>{data.seo_content.meta_title}</title>
        <meta name="description" content={data.seo_content.meta_desc} />
        <meta name="keywords" content={data.seo_content.keywords} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <header className="bg-slate-900 text-white pt-10 pb-16 px-4 relative overflow-hidden">
        <div className="absolute -top-20 -right-16 h-64 w-64 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-16 h-64 w-64 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto">
          <nav className="flex items-center gap-2 text-xs text-slate-300 mb-5 font-bold uppercase tracking-wider">
            <Link to="/" className="no-underline text-slate-300 hover:text-white">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/city-comparisons" className="no-underline text-slate-300 hover:text-white">Comparisons</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-white">{title}</span>
          </nav>

          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-widest mb-4">
            <Scale className="h-4 w-4" /> Rentsmart.ie City Comparison
          </div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4">{data.seo_content.h1}</h1>
          <p className="text-slate-300 text-lg max-w-3xl leading-relaxed">{data.analysis.relocation_context}</p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="text-xs font-black uppercase tracking-widest text-slate-400">Share:</span>
            <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
              <FacebookIcon size={30} round />
            </FacebookShareButton>
            <TwitterShareButton url={shareUrl} title={shareText}>
              <TwitterIcon size={30} round />
            </TwitterShareButton>
            <WhatsappShareButton url={shareUrl} title={shareText}>
              <WhatsappIcon size={30} round />
            </WhatsappShareButton>
            <LinkedinShareButton url={shareUrl} title={title} summary={shareText} source="RentSmart.ie">
              <LinkedinIcon size={30} round />
            </LinkedinShareButton>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 -mt-8 pb-16">
        <section className="grid md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8 mt-16">
          {quickWins.map((item) => (
            <div
              key={item.title}
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:-translate-y-1 hover:shadow-xl transition-all"
            >
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-black text-slate-500 mb-2">
                {item.icon} {item.title}
              </div>
              <div className="text-xl font-black text-slate-900 mb-2">{item.winner}</div>
              <p className="text-xs text-slate-600">{item.detail}</p>
            </div>
          ))}
        </section>

        <section className="rounded-[2.5rem] border border-slate-100 bg-white p-6 md:p-8 shadow-xl shadow-slate-200/40 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
            <h2 className="text-2xl font-black">At a Glance: {data.city_a.name} vs {data.city_b.name}</h2>
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'all', label: 'All' },
                { id: 'financial', label: 'Financial' },
                { id: 'relocation', label: 'Relocation' },
                { id: 'lifestyle', label: 'Lifestyle' },
              ].map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setLens(item.id as ComparisonLens)}
                  className={`rounded-full px-3 py-1.5 text-xs font-black uppercase tracking-wider border transition-all ${
                    lens === item.id
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300 hover:text-emerald-700'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse md:min-w-[680px]">
              <thead className="hidden md:table-header-group">
                <tr>
                  <th className="text-left text-xs uppercase tracking-wider text-slate-500 pb-3">Metric</th>
                  <th className="text-left text-xs uppercase tracking-wider text-slate-500 pb-3">{data.city_a.name}</th>
                  <th className="text-left text-xs uppercase tracking-wider text-slate-500 pb-3">{data.city_b.name}</th>
                  <th className="text-left text-xs uppercase tracking-wider text-slate-500 pb-3">Winner</th>
                </tr>
              </thead>
              <tbody className="block md:table-row-group">
                {filteredRows.map((row) => {
                  const values = getMetricNumbers(row.label);
                  const max = Math.max(values.a, values.b, 1);
                  const aWidth = Math.round((values.a / max) * 100);
                  const bWidth = Math.round((values.b / max) * 100);
                  return (
                    <tr
                      key={row.label}
                      className="mb-4 block rounded-2xl border border-slate-100 bg-slate-50/60 p-4 md:mb-0 md:table-row md:rounded-none md:border-0 md:bg-transparent md:p-0 md:border-t md:border-slate-100"
                    >
                      <td className="block md:table-cell py-2 md:py-4 font-bold text-slate-800">
                        <div className="text-sm md:text-base">{row.label}</div>
                        <div className="mt-2 space-y-1">
                          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full rounded-full bg-emerald-500" style={{ width: `${aWidth}%` }} />
                          </div>
                          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full rounded-full bg-sky-500" style={{ width: `${bWidth}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="block md:table-cell py-2 md:py-4 text-slate-700 font-semibold">
                        <span className="md:hidden text-xs uppercase tracking-wider text-slate-400 block">{data.city_a.name}</span>
                        <span className="text-sm md:text-base">{row.cityAValue}</span>
                      </td>
                      <td className="block md:table-cell py-2 md:py-4 text-slate-700 font-semibold">
                        <span className="md:hidden text-xs uppercase tracking-wider text-slate-400 block">{data.city_b.name}</span>
                        <span className="text-sm md:text-base">{row.cityBValue}</span>
                      </td>
                      <td className="block md:table-cell py-2 md:py-4">
                        <span className="md:hidden text-xs uppercase tracking-wider text-slate-400 block">Winner</span>
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-700 border border-slate-200">
                          {row.winner === 'city_a'
                            ? data.city_a.name
                            : row.winner === 'city_b'
                              ? data.city_b.name
                              : winnerPill(row.winner)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filteredRows.length === 0 ? (
            <p className="mt-4 text-sm text-slate-500 font-semibold">
              No metrics in this lens. Switch to another filter.
            </p>
          ) : null}
        </section>

        <section className="grid lg:grid-cols-12 gap-8 mb-8">
          <div className="lg:col-span-8 space-y-8">
            <div className="rounded-[2.5rem] border border-slate-100 bg-white p-6 md:p-8 shadow-sm">
              <h2 className="text-2xl font-black mb-4">Who Wins?</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                  <h3 className="font-black text-emerald-900 mb-2">Best for Young Professionals</h3>
                  <p className="text-sm text-emerald-900/90">{data.verdicts.young_professionals}</p>
                </div>
                <div className="rounded-2xl border border-sky-200 bg-sky-50 p-5">
                  <h3 className="font-black text-sky-900 mb-2">Best for Families</h3>
                  <p className="text-sm text-sky-900/90">{data.verdicts.families}</p>
                </div>
                <div className="rounded-2xl border border-violet-200 bg-violet-50 p-5">
                  <h3 className="font-black text-violet-900 mb-2">Best for Investors</h3>
                  <p className="text-sm text-violet-900/90">{data.verdicts.investors}</p>
                </div>
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
                  <h3 className="font-black text-amber-900 mb-2">Overall Directional Winner</h3>
                  <p className="text-sm text-amber-900/90">{data.verdicts.overall}</p>
                </div>
              </div>
              {data.ai_enrichment?.winner_breakdown && (
                <div className="mt-6 rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4 md:p-5">
                  <div className="flex items-center gap-2 mb-4 text-slate-700">
                    <Sparkles className="h-4 w-4 text-emerald-600" />
                    <p className="text-xs font-black uppercase tracking-widest">Interactive Reason Explorer</p>
                  </div>

                  <div className="grid md:grid-cols-3 gap-3 mb-4">
                    {winnerReasonCards.map((item) => {
                      const isActive = activeReasonData?.key === item.key;
                      const activeToneClass =
                        item.tone === 'emerald'
                          ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
                          : item.tone === 'sky'
                            ? 'border-sky-300 bg-sky-50 text-sky-900'
                            : 'border-violet-300 bg-violet-50 text-violet-900';
                      return (
                        <button
                          key={item.key}
                          type="button"
                          onClick={() => setActiveReason(item.key)}
                          className={`text-left rounded-2xl border px-4 py-3 transition-all ${
                            isActive
                              ? `${activeToneClass} shadow-sm`
                              : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                          }`}
                        >
                          <p className="text-[11px] uppercase tracking-wider font-black mb-1">{item.label}</p>
                          <p className="text-xs opacity-90">
                            {isActive ? 'Selected' : 'Tap to inspect reason'}
                          </p>
                        </button>
                      );
                    })}
                  </div>

                  {activeReasonData ? (
                    <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4">
                      <p className="text-[10px] uppercase tracking-widest font-black text-slate-400 mb-2">
                        {activeReasonData.label} Breakdown
                      </p>
                      <p className="text-sm md:text-base text-slate-700 leading-relaxed">
                        {activeReasonData.text}
                      </p>
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {data.ai_enrichment?.salary_needed_estimate ? (
              <div className="rounded-[2.5rem] border border-slate-100 bg-white p-6 md:p-8 shadow-sm">
                <h2 className="text-2xl font-black mb-4">Salary Needed Signal</h2>
                <p className="text-sm text-slate-600 leading-relaxed mb-5">
                  {data.ai_enrichment.salary_needed_estimate.summary}
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                    <p className="text-[10px] uppercase tracking-wider font-black text-emerald-700 mb-1">{data.city_a.name}</p>
                    <p className="text-3xl font-black text-emerald-800">
                      €{data.ai_enrichment.salary_needed_estimate.city_a_monthly_net_target.toLocaleString()}
                    </p>
                    <p className="text-xs text-emerald-900/80 mt-1">estimated monthly net target</p>
                  </div>
                  <div className="rounded-2xl border border-sky-200 bg-sky-50 p-5">
                    <p className="text-[10px] uppercase tracking-wider font-black text-sky-700 mb-1">{data.city_b.name}</p>
                    <p className="text-3xl font-black text-sky-800">
                      €{data.ai_enrichment.salary_needed_estimate.city_b_monthly_net_target.toLocaleString()}
                    </p>
                    <p className="text-xs text-sky-900/80 mt-1">estimated monthly net target</p>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="rounded-[2.5rem] border border-slate-100 bg-white p-6 md:p-8 shadow-sm">
              <h2 className="text-2xl font-black mb-4 flex items-center gap-2">
                <Gem className="h-5 w-5 text-emerald-600" /> Hidden Gems: Local Highlights
              </h2>
              <div className="grid md:grid-cols-2 gap-5">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <h3 className="font-black text-slate-900 mb-3">{data.city_a.name}</h3>
                  <ul className="space-y-2 text-sm text-slate-700">
                    {data.hidden_gems.city_a.map((item, idx) => (
                      <li key={`${item}-${idx}`}>• {item}</li>
                    ))}
                  </ul>
                  {data.ai_enrichment?.micro_neighborhood_hooks?.city_a?.length ? (
                    <ul className="space-y-2 text-sm text-emerald-800 mt-3 pt-3 border-t border-slate-200">
                      {data.ai_enrichment.micro_neighborhood_hooks.city_a.map((item, idx) => (
                        <li key={`${item}-${idx}`}>• {item}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <h3 className="font-black text-slate-900 mb-3">{data.city_b.name}</h3>
                  <ul className="space-y-2 text-sm text-slate-700">
                    {data.hidden_gems.city_b.map((item, idx) => (
                      <li key={`${item}-${idx}`}>• {item}</li>
                    ))}
                  </ul>
                  {data.ai_enrichment?.micro_neighborhood_hooks?.city_b?.length ? (
                    <ul className="space-y-2 text-sm text-emerald-800 mt-3 pt-3 border-t border-slate-200">
                      {data.ai_enrichment.micro_neighborhood_hooks.city_b.map((item, idx) => (
                        <li key={`${item}-${idx}`}>• {item}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="rounded-[2.5rem] border border-slate-100 bg-white p-6 md:p-8 shadow-sm">
              <h2 className="text-2xl font-black mb-4">FAQ</h2>
              <div className="space-y-3">
                {data.seo_content.faq.map((faq, index) => (
                  <details key={`${faq.question}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <summary className="cursor-pointer font-bold text-slate-800">{faq.question}</summary>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{faq.answer}</p>
                  </details>
                ))}
              </div>
            </div>

            <PseoEmailCapture
              pageType="city-comparisons"
              pageSlug={data.slug}
              locationName={`${data.city_a.name} vs ${data.city_b.name}`}
              contextHeadline={`Get ${data.city_a.name} vs ${data.city_b.name} Comparison Updates`}
              contextReason={`Receive Irish market updates for this city pair, including affordability and commute trade-off changes, plus new city-comparison reports matched to your user type.`}
            />
          </div>

          <aside className="lg:col-span-4 space-y-6">
            <div className="sticky top-8 space-y-6">
              <div className="rounded-3xl bg-gradient-to-br from-emerald-700 to-emerald-900 text-white p-6 shadow-xl shadow-emerald-900/20">
                <h3 className="text-xl font-black mb-2">Search Properties Now</h3>
                <p className="text-emerald-100/90 text-sm mb-5 leading-relaxed">
                  Next step: immediately compare live listings in both cities while this
                  comparison is fresh.
                </p>

                <div className="space-y-3 mb-4">
                  <a
                    href={cityARentPath}
                    className="no-underline flex items-center justify-between rounded-xl bg-white text-emerald-800 px-4 py-3 font-black text-sm hover:bg-emerald-50 transition-all"
                  >
                    Rent in {data.city_a.name} <ArrowRight className="h-4 w-4" />
                  </a>
                  <a
                    href={cityBRentPath}
                    className="no-underline flex items-center justify-between rounded-xl bg-white text-emerald-800 px-4 py-3 font-black text-sm hover:bg-emerald-50 transition-all"
                  >
                    Rent in {data.city_b.name} <ArrowRight className="h-4 w-4" />
                  </a>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <a
                    href={cityASalePath}
                    className="no-underline inline-flex items-center justify-center rounded-lg border border-white/30 bg-white/10 px-3 py-2 text-xs font-black text-white hover:bg-white/20 transition-all"
                  >
                    Buy {data.city_a.name}
                  </a>
                  <a
                    href={cityBSalePath}
                    className="no-underline inline-flex items-center justify-center rounded-lg border border-white/30 bg-white/10 px-3 py-2 text-xs font-black text-white hover:bg-white/20 transition-all"
                  >
                    Buy {data.city_b.name}
                  </a>
                </div>
              </div>

              <div className="rounded-3xl bg-emerald-700 text-white p-6 shadow-xl">
                <h3 className="text-xl font-black mb-2 flex items-center gap-2"><Home className="h-5 w-5" /> Deep Links</h3>
                <div className="space-y-3 text-sm">
                  <Link to={data.links.city_a_cost_of_living} className="no-underline block rounded-xl bg-white text-emerald-700 px-4 py-3 font-black hover:bg-emerald-50">
                    {data.city_a.name} Cost of Living
                  </Link>
                  <Link to={data.links.city_b_cost_of_living} className="no-underline block rounded-xl bg-white text-emerald-700 px-4 py-3 font-black hover:bg-emerald-50">
                    {data.city_b.name} Cost of Living
                  </Link>
                  <Link to={data.links.city_a_valuation} className="no-underline block rounded-xl bg-white text-emerald-700 px-4 py-3 font-black hover:bg-emerald-50">
                    {data.city_a.name} Valuation Report
                  </Link>
                  <Link to={data.links.city_b_valuation} className="no-underline block rounded-xl bg-white text-emerald-700 px-4 py-3 font-black hover:bg-emerald-50">
                    {data.city_b.name} Valuation Report
                  </Link>
                </div>
              </div>

              {data.related_comparisons?.length ? (
                <div className="rounded-3xl border border-slate-200 bg-white p-6">
                  <h3 className="text-xl font-black text-slate-900 mb-3 flex items-center gap-2">
                    <Compass className="h-5 w-5 text-sky-600" /> Related Comparisons
                  </h3>
                  <div className="space-y-2">
                    {data.related_comparisons.map((item) => (
                      <Link
                        key={item.slug}
                        to={item.path}
                        className="no-underline flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-700 hover:border-emerald-300 hover:text-emerald-700"
                      >
                        {item.title} <ArrowRight className="h-4 w-4" />
                      </Link>
                    ))}
                  </div>
                </div>
              ) : null}

              {data.ai_enrichment?.moving_playbook?.length ? (
                <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-emerald-50/40 p-6 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="h-8 w-8 rounded-full bg-emerald-600 text-white text-xs font-black flex items-center justify-center">
                      GO
                    </div>
                    <h3 className="text-xl font-black text-slate-900">Moving Playbook</h3>
                  </div>

                  <div className="space-y-3">
                    {data.ai_enrichment.moving_playbook.map((item, idx) => (
                      <div
                        key={`${item}-${idx}`}
                        className="rounded-2xl border border-slate-200 bg-white p-4 hover:-translate-y-0.5 hover:shadow-md transition-all"
                      >
                        <div className="flex items-start gap-3">
                          <div className="h-7 w-7 shrink-0 rounded-full bg-emerald-100 text-emerald-700 text-xs font-black flex items-center justify-center border border-emerald-200">
                            {idx + 1}
                          </div>
                          <p className="text-sm font-semibold text-slate-700 leading-relaxed pt-0.5">
                            {item}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="mt-4 text-xs text-slate-500 font-medium">
                    Use these steps as a practical checklist before committing to a move.
                  </p>
                </div>
              ) : null}

              {data.ai_enrichment?.related_search_intents?.length ? (
                <div className="rounded-3xl border border-slate-200 bg-white p-6">
                  <h3 className="text-xl font-black text-slate-900 mb-3 flex items-center gap-2">
                    <Share2 className="h-5 w-5 text-violet-600" /> Popular Comparison Searches
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {data.ai_enrichment.related_search_intents.map((intent, idx) => (
                      <span
                        key={`${intent}-${idx}`}
                        className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-bold text-slate-600"
                      >
                        {intent}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </aside>
        </section>
      </main>
    </div>
  );
};

export default CityComparisonPage;
